import { useEffect, useState } from 'react';
import MiscAPI from '../../axios/misc';
import config from '../../config';

// What a listing shows depends on its listing type and property type: the server keeps one form for
// each pair, naming its fields, the values offered in its lists and its checkboxes.

// the forms and property types change only in the admin, so each language is asked for once
const cache = {};
const load = (lang) => {
    if (!cache[lang]) {
        cache[lang] = Promise.all([MiscAPI.getListingForms(lang), MiscAPI.getPropertyTypeOptions(lang)])
            .then(([forms, types]) => ({ forms: forms.data, types: types.data }))
            .catch(err => {
                delete cache[lang];
                throw err;
            });
    }
    return cache[lang];
};

export const useListingForms = (lang) => {
    const [data, setData] = useState({ forms: [], types: [], loaded: false });
    useEffect(() => {
        let cancelled = false;
        load(lang).then(result => !cancelled && setData({ ...result, loaded: true })).catch(err => console.log(err));
        return () => { cancelled = true; };
    }, [lang]);
    return data;
};

export const findForm = (forms, listingType, propertyType) =>
    forms.find(form => form.listing_type === Number(listingType) && form.property_type === Number(propertyType));

// the property types that can be listed for a listing type: the top-level ones, a group such as business
// premises standing for its types
export const typesFor = (forms, types, listingType) => {
    const offered = new Set(forms.filter(form => form.listing_type === Number(listingType)).map(form => form.property_type));
    const parents = new Set(types.filter(type => offered.has(type.id)).map(type => type.parent).filter(Boolean));
    return types.filter(type => !type.parent && (offered.has(type.id) || parents.has(type.id)));
};
export const childTypes = (forms, types, listingType, parent) => {
    const offered = new Set(forms.filter(form => form.listing_type === Number(listingType)).map(form => form.property_type));
    return types.filter(type => type.parent === parent && offered.has(type.id));
};

export const hasField = (form, name) => !!form?.fields.some(field => field.name === name);

// the lists of numbers a listing picks from; the value kept is the position in the list
const numbers = (count) => Array.from({ length: count }, (_, i) => ({ id: i + 1, label: i + 1 === count ? `${count}+` : String(i + 1) }));
export const NUMBER_OPTIONS = {
    num_rooms: config.num_rooms_options,
    num_bathrooms: config.num_bathrooms_options,
    offices: numbers(15),
    phone_lines: numbers(5),
    guests: numbers(16),
};

// SUT, PSUT, PR and VPR are words in the "listing" texts, the floors are numbers
export const floorOptions = (t) => config.floor_options.map(option => ({
    id: option.id,
    label: /^\d+$/.test(option.label) ? option.label : t(option.label, { ns: 'listing' }),
}));

export const PLOT_UNITS = ['ar', 'm2', 'ha', 'jutro'];
// the size of each unit in m²; the server keeps a plot in m² with the unit it was entered in
const PLOT_UNIT_M2 = { m2: 1, ar: 100, ha: 10000, jutro: 5754.64 };
export const plotUnitLabel = (unit) => unit === 'm2' ? 'm²' : unit;

// "4 ar" for a plot kept as 400 m² entered in ares
export const plotAreaLabel = (listing) => {
    const m2 = parseFloat(listing.plot_area);
    if (!m2) return null;
    const unit = listing.plot_area_unit || 'm2';
    const value = Math.round(m2 / PLOT_UNIT_M2[unit] * 100) / 100;
    return `${String(value).replace('.', ',')} ${plotUnitLabel(unit)}`;
};

export const numberLabel = (name, value) => NUMBER_OPTIONS[name]?.find(option => option.id === value)?.label;

// the floor as "3 / 5", or only one end when the other is missing
export const floorLabel = (listing, t) => {
    const floors = floorOptions(t);
    const floor = floors.find(option => option.id === listing.floor)?.label;
    const max = floors.find(option => option.id === listing.max_floor)?.label;
    return floor && max ? `${floor} / ${max}` : floor || null;
};
